/**
 * FeedbackCollector
 * Manages user feedback (thumbs up/down) for encouragement messages
 */

import { storage } from '../storage/StorageAdapter';
import { EncouragementMessage, FeedbackRating } from '../../models/EncouragementMessage';

// Storage key for encouragement messages
const ENCOURAGEMENT_KEY = 'encouragement_messages';

/**
 * Get all encouragement messages
 */
export const getAllMessages = async (): Promise<EncouragementMessage[]> => {
  const messagesJson = await storage.getString(ENCOURAGEMENT_KEY);
  return messagesJson ? JSON.parse(messagesJson) : [];
};

/**
 * Get messages for a specific account
 */
export const getAccountMessages = async (accountId: string): Promise<EncouragementMessage[]> => {
  const allMessages = await getAllMessages();
  return allMessages.filter(m => m.accountId === accountId);
};

/**
 * Save a new message
 */
export const saveMessage = async (message: EncouragementMessage): Promise<void> => {
  const messages = await getAllMessages();
  messages.push(message);
  await storage.setString(ENCOURAGEMENT_KEY, JSON.stringify(messages));
};

/**
 * Record feedback for a message
 */
export const recordFeedback = async (
  messageId: string,
  rating: FeedbackRating
): Promise<void> => {
  const messages = await getAllMessages();
  const message = messages.find(m => m.id === messageId);

  if (message) {
    message.feedbackRating = rating;
    message.feedbackAt = Date.now();
    await storage.setString(ENCOURAGEMENT_KEY, JSON.stringify(messages));
  }
};

/**
 * Get feedback statistics for a pattern
 */
export const getPatternFeedback = async (
  accountId: string,
  pattern: string
): Promise<{
  totalShown: number;
  thumbsUp: number;
  thumbsDown: number;
  noFeedback: number;
}> => {
  const messages = await getAccountMessages(accountId);
  const patternMessages = messages.filter(m => m.pattern === pattern);

  return {
    totalShown: patternMessages.length,
    thumbsUp: patternMessages.filter(m => m.feedbackRating === 'thumbs_up').length,
    thumbsDown: patternMessages.filter(m => m.feedbackRating === 'thumbs_down').length,
    noFeedback: patternMessages.filter(m => m.feedbackRating === null).length,
  };
};

/**
 * Clear all messages (for testing or reset)
 */
export const clearAllMessages = async (): Promise<void> => {
  await storage.delete(ENCOURAGEMENT_KEY);
};
